import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { getAnsweredAuthorizationsNotification } from "../../actions/authorizations";
import { getConnectivityErrorsNotProcessedCount } from "../../actions/connectivityResponse";
import { getPendingMessagingCount } from "../../actions/messaging";
import { getUserProviders } from "../../actions/providers";
import { changeDrawerStatus } from "../../actions/userPreferences";
import ArtProvidedServiceContainer from "../commons/artProvidedServices/artProvidedServiceContainer";
import Error from "../commons/error/error";
import LoadingComponent from "../commons/loadingData/loading";
import ProviderProvidedServiceContainer from "../commons/providerProvidedServices/providerProvidedServiceContainer";
import { StyledAuthRoutes } from "../styled/layout";
import ImportIetContainer from "../ui/accident/importIet/ImportIetContainer";
import Accident from "../ui/accident/inProgress/accidentContainer";
import NewAccidentsContainer from "../ui/accident/newAccident/newAccidentContainer";
import SearchContainer from "../ui/accident/search/searchContainer";
import AuthorizedAttendanceContainer from "../ui/appointment/attendance/authorizedAttendance/authorizedAttendanceContainer";
import QRContainer from "../ui/appointment/attendance/QRContainer";
import PendingAppointmentsContainer from "../ui/appointment/pending/pendingContainer";
import AppointmentsSearchContainer from "../ui/appointment/search/searchContainer";
import SupportAppointmentsContainer from "../ui/appointment/support/appointmentsSearchContainer";
import ArtSearchContainer from "../ui/art/siniestros/ArtSearchContainer";
import AuditoriaPrestadoresContainer from "../ui/art/auditoriaPrestadores/auditoriaPrestadoresContainer";
import ArtNewAccidentContainer from "../ui/art/siniestros/artNewAccidentContainer";
import AgenciasMapFrame from "../ui/art/traslados/pendientes/AgenciasAsignacionMap/AgenciasMapFrame";
import { AGENCIAS_FRAME_PATH } from "../ui/art/traslados/pendientes/agenciasFramePath";
import PendientesTrasladosContainer from "../ui/art/traslados/pendientes/pendientesTrasladosContainer";
import BuscarTrasladosContainer from "../ui/art/traslados/buscar/buscarTrasladosContainer";
import ReportesTrasladosContainer from "../ui/art/traslados/reportes/reportesTrasladosContainer";
import AgenciasContainer from "../ui/art/traslados/agencias/AgenciasContainer";
import ArtAccidentRoutes from "./artAccidentRoutes";
import ArtFormsConfigurationContainer from "../ui/artsConfiguration/formsConfiguration/artFormsConfigurationContainer";
import AnsweredContainer from "../ui/authorizations/answered/answeredContainer";
import PendingAuthorizationsContainer from "../ui/authorizations/pending/pendingAuthorizationsContainer";
import AuthorizationsSearchContainer from "../ui/authorizations/search/searchContainer";
import ConnectivityErrorsContainer from "../ui/connectivityResponses/errors/ConnectivityErrorsContainer";
import SearchConnectivityErrorsContainer from "../ui/connectivityResponses/search/SearchConnectivityErrorsContainer";
import DashboardCrudArt from "../ui/crudArt/dashboardCrudArtContainer";
import DashboardContainer from "../ui/dashboard/container";
import EmailContainer from "../ui/email/emailContainer";
import FilesContainer from "../ui/files/FilesContainer";
import AuthorizedResponsiveFormContainer from "../ui/forms/responsiveFormContainer/authorizedResponsiveFormContainer/authorizedResponsiveFormContainer";
import MyArtsContainer from "../ui/myArts/myArtsContainer";
import NotificacionBuscarContainer from "../ui/notificaciones/buscar/notificacionBuscarContainer";
import NotificacionNuevoContainer from "../ui/notificaciones/nuevo/notificacionNuevoContainer";
import NotificacionPlantillasContainer from "../ui/notificaciones/plantillas/notificacionPlantillasContainer";
import NovedadesPrestadorSearchContainer from "../ui/novedades/prestador/novedadesPrestadorSearchContainer";
import ProvidersResponseContainer from "../ui/novedades/respuestas/providersResponseContainer";
import NovedadesSearchContainer from "../ui/novedades/search/novedadesSearchContainer";
import XmlNewsContainer from "../ui/novedades/xml/xmlNewsContainer";
import ProviderContainer from "../ui/providers/providerContainer";
import DatacenterPrestadoresContainer from "../ui/providers/datacenter/datacenterPrestadoresContainer";
import ProviderStatsContainer from "../ui/providers/statistics/providerStatsContainer";
import ProviderUserTableContainer from "../ui/providerUserTable/providerUserTableContainer";
import ServiceMonitorSearchContainer from "../ui/serviceMonitor/serviceMonitorSearchContainer";
import StatisticContainer from "../ui/statistic/statisticContainer";
import TutorialsContainer from "../ui/tutorials/tutorialsContainer";
import BlockedUsersContainer from "../ui/users/blockedUsersContainer";
import PendingUsersContainer from "../ui/users/pendingUsersContainer";
import UsersContainer from "../ui/users/usersContainer";
import XmlValidationContainer from "../ui/xml/validation/xmlValidationContainer";
import MessagingContainer from "../ui/messaging/messagingContainer";
import NewConversationContainer from "../ui/messaging/newConversationContainer";
import MainLayout from "./layouts/mainLayout";
import SnackbarUtils from "../../utils/snackbarUtils";

const AuthRoutes = () => {
  const dispatch = useDispatch();

  const providers = useSelector((state) => state.providers);
  const drawerStatus = useSelector((state) => state.userPreferences.drawer);
  const roles = useSelector((state) => state.auth.roles);
  const art = useSelector((state) => state.art);

  const [mobileOpen, setMobileOpen] = useState(drawerStatus);

  useEffect(() => {
    if (roles && roles.PRESTADOR) dispatch(getUserProviders());
  }, [roles]);

  const getDrawerNotifications = () => {
    dispatch(getAnsweredAuthorizationsNotification());
    dispatch(getConnectivityErrorsNotProcessedCount());
    if (providers.selectedProvider) {
      dispatch(getPendingMessagingCount(providers.selectedProvider));
    }
  };

  const pendingCount = useSelector((state) => state.messaging.pendingCount);
  const [prevPendingCount, setPrevPendingCount] = useState(pendingCount);

  useEffect(() => {
    if (pendingCount > prevPendingCount) {
      SnackbarUtils.info("Tiene mensajes nuevos");
    }
    setPrevPendingCount(pendingCount);
  }, [pendingCount]);

  useEffect(() => {
    let interval;
    let fastInterval;
    let longInterval;

    if (roles && roles.PRESTADOR) {
      getDrawerNotifications();
      interval = setInterval(getDrawerNotifications, 7200000);

      if (providers.selectedProvider && typeof CONFIG !== "undefined") {
        if (CONFIG.shortMessagingNotificationTimer) {
          fastInterval = setInterval(() => {
            dispatch(getPendingMessagingCount(providers.selectedProvider));
          }, CONFIG.shortMessagingNotificationTimer);
        }

        const longTimer = CONFIG.longMessagingNotificationTimer || 1800000;
        longInterval = setInterval(() => {
          dispatch(getPendingMessagingCount(providers.selectedProvider)).then((action) => {
            const count = action.value !== undefined ? action.value : action.payload;
            if (count > 0) {
              SnackbarUtils.info("Tiene mensajes sin leer");
            }
          }).catch(() => {});
        }, longTimer);
      }
    }

    return () => {
      if (interval) clearInterval(interval);
      if (fastInterval) clearInterval(fastInterval);
      if (longInterval) clearInterval(longInterval);
    };
  }, [providers.selectedProvider, roles]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prevState) => !prevState);
    dispatch(changeDrawerStatus(!mobileOpen));
  }, [mobileOpen]);

  if (providers.didInvalidate && providers.list.length === 0) return <Error />;

  if (!(roles.SOPORTE || roles.ADMIN) && (providers.isFetching || art.isFetching)) return <LoadingComponent />;

  if (roles && roles.SISTEMA) {
    return (
      <Routes>
        <Route path="/attendance" element={<AuthorizedAttendanceContainer />} />
        <Route path="*" element={<Navigate to="/attendance" />} />
      </Routes>
    )
  }

  return (
    <StyledAuthRoutes.Root>
      <Routes>
        <Route
          path="/responsiveform/:encryptedParameters"
          element={<AuthorizedResponsiveFormContainer />}
        />
        <Route path={AGENCIAS_FRAME_PATH} element={<AgenciasMapFrame />} />
        <Route element={<MainLayout mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />}>
          <Route path="/tutorials" element={<TutorialsContainer />} />
          {roles && roles.PRESTADOR && (
            <>
              <Route path="/home" element={<DashboardContainer />} />
              <Route path="/accidents/current" element={<Accident />} />
              <Route path="/accidents/search" element={<SearchContainer />} />
              <Route path="/accidents/new" element={<NewAccidentsContainer />} />
              <Route
                path="/accidents/importIet"
                element={<ImportIetContainer />}
              />
              <Route
                path="/authorizations/pending"
                element={<PendingAuthorizationsContainer />}
              />
              <Route
                path="/authorizations/answered"
                element={<AnsweredContainer />}
              />
              <Route
                path="/authorizations/search"
                element={<AuthorizationsSearchContainer />}
              />
              <Route
                path="/appointments/pending"
                element={<PendingAppointmentsContainer />}
              />
              <Route
                path="/appointments/search"
                element={<AppointmentsSearchContainer />}
              />
              <Route path="/appointments/QR" element={<QRContainer />} />
              <Route path="/myWris" element={<MyArtsContainer />} />
              <Route path="/statistic" element={<StatisticContainer />} />
              <Route path="/emails" element={<EmailContainer />} />
              <Route
                path="/respuestasConectividad/errors"
                element={<ConnectivityErrorsContainer />}
              />
              <Route
                path="/respuestasConectividad/search"
                element={<SearchConnectivityErrorsContainer />}
              />
              <Route
                path="/usuarios"
                element={<ProviderUserTableContainer />}
              />
              <Route
                path="/messaging/pending"
                element={<MessagingContainer pending={true} />}
              />
              <Route
                path="/messaging/new"
                element={<NewConversationContainer />}
              />
              <Route
                path="/messaging/waiting"
                element={<MessagingContainer waiting={true} />}
              />
              <Route
                path="/messaging/search"
                element={<MessagingContainer search={true} />}
              />
              <Route path="*" element={<Navigate to="/home" />} />
            </>
          )}
          {roles && roles.ADMIN && roles.tipo === "Administrador" && (
            <>
              <Route path="/configuration/users" element={<UsersContainer />} />
              <Route path="/configuration/blockedUsers" element={<BlockedUsersContainer />} />
              <Route path="/configuration/pendingUsers" element={<PendingUsersContainer />} />
              <Route path="/configuration/arts/arts" element={<DashboardCrudArt />} />
              <Route path="/configuration/arts/services" element={<ArtProvidedServiceContainer />} />
              <Route path="/configuration/providers" element={<ProviderContainer />} />
              <Route path="/configuration/statistics" element={<ProviderStatsContainer />} />
              <Route path="/configuration/files" element={<FilesContainer />} />
              <Route path="/accidents/search" element={<ArtSearchContainer />} />
              <Route path="/novedades/search" element={<NovedadesSearchContainer />} />
              <Route path="/novedades/temporal" element={<NovedadesPrestadorSearchContainer />} />
              <Route path="/novedades/xml" element={<XmlNewsContainer />} />
              <Route path="/novedades/respuestas" element={<ProvidersResponseContainer />} />
              <Route path="/configuration/serviceMonitor" element={<ServiceMonitorSearchContainer />} />
              <Route path="/turnos/search" element={<SupportAppointmentsContainer />} />
              <Route path="/xml/validation" element={<XmlValidationContainer />} />
              <Route path="/configuration/providerServices" element={<ProviderProvidedServiceContainer />} />
              <Route path="/configuration/providers/datacenter" element={<DatacenterPrestadoresContainer />} />
              <Route path="/configuration/artConfiguration/forms" element={<ArtFormsConfigurationContainer />} />
              <Route path="/notificaciones/nuevo" element={<NotificacionNuevoContainer />} />
              <Route path="/notificaciones/buscar" element={<NotificacionBuscarContainer />} />
              <Route path="/notificaciones/plantillas" element={<NotificacionPlantillasContainer />} />
              <Route path="/art/traslados/agencias" element={<AgenciasContainer />} />
              <Route path="*" element={<Navigate to="/configuration/users" />} />
            </>
          )}
          {roles && roles.SOPORTE && roles.tipo === "Soporte" && (
            <>
              <Route path="/configuration/users" element={<UsersContainer />} />
              <Route path="/accidents/search" element={<ArtSearchContainer />} />
              <Route path="/novedades/search" element={<NovedadesSearchContainer />} />
              <Route path="/novedades/temporal" element={<NovedadesPrestadorSearchContainer />} />
              <Route path="/novedades/xml" element={<XmlNewsContainer />} />
              <Route path="/novedades/respuestas" element={<ProvidersResponseContainer />} />
              <Route path="/turnos/search" element={<SupportAppointmentsContainer />} />
              <Route path="/xml/validation" element={<XmlValidationContainer />} />
              <Route path="/configuration/serviceMonitor" element={<ServiceMonitorSearchContainer />} />
              <Route path="/art/traslados/agencias" element={<AgenciasContainer />} />
              <Route path="*" element={<Navigate to="/configuration/users" />} />
            </>
          )}
          {roles?.PRESTADORTRASLADO && (
            <>
              <Route path="/home" element={<></>} />
              <Route path="*" element={<Navigate to="/home" />} />
            </>
          )}
          {roles?.ART && (
            <>
              {ArtAccidentRoutes()}
              {roles.tipo === "Supervisor Art" && (
                <Route path="/art/auditoria-prestadores" element={<AuditoriaPrestadoresContainer />} />
              )}
              <Route path="*" element={<Navigate to="/art/prestadores" />} />
            </>
          )}
          {(roles?.AGENTETRASLADOS || roles?.GESTORSINIESTROS || roles?.PRESTADORTRASLADO) && (
            <>
              {ArtAccidentRoutes()}
              <Route
                path="/art/accidents/new"
                element={<ArtNewAccidentContainer />}
              />
              <Route
                path="/art/traslados/pendientes"
                element={<PendientesTrasladosContainer />}
              />
              <Route
                path="/art/traslados/search"
                element={<BuscarTrasladosContainer />}
              />
              <Route
                path="/art/traslados/reports"
                element={<ReportesTrasladosContainer />}
              />
              <Route
                path="/art/traslados/agencias"
                element={<AgenciasContainer />}
              />
              <Route
                path="*"
                element={<Navigate to={roles?.PRESTADORTRASLADO ? "/art/traslados/search" : "/art/traslados/pendientes"} />}
              />
            </>
          )}
        </Route>
      </Routes>
    </StyledAuthRoutes.Root >
  );
};

export default AuthRoutes;
